// K-Beauty Skincare Guide
// Based on K-Cosmetic Product Analysis documentation

export interface KBeautyStep {
  id: string
  step: number
  name: string
  icon: string
  color: string
  briefDescription: string
  fullDescription: string
  products: {
    name: string
    types: string[]
    description?: string
  }[]
  tips: string[]
  amPm: {
    am: string
    pm: string
  }
}

export const KBEAUTY_STEPS: KBeautyStep[] = [
  {
    id: 'cleanse',
    step: 1,
    name: 'Cleanse',
    icon: '🧴',
    color: '#60A5FA',
    briefDescription: 'Remove impurities and prep your skin',
    fullDescription: 'The foundation of K-beauty is the double cleanse. Oil-based cleansers dissolve makeup, sunscreen, and sebum, while water-based cleansers remove sweat and impurities. This two-step process ensures a truly clean canvas without stripping your skin.',
    products: [
      {
        name: 'Cleanser',
        types: ['Oil Cleanser', 'Balm Cleanser', 'Cream Cleanser', 'Gel Cleanser', 'Foam Cleanser', 'Powder Cleanser', 'Cleansing Tissue'],
        description: 'Oil-based for PM (removes makeup/sunscreen), water-based for AM'
      },
      {
        name: 'Exfoliator',
        types: ['Peeling Gel', 'Scrub', 'AHA/BHA Toner', 'Exfoliating Pad'],
        description: 'Use 2-3 times per week to remove dead skin cells'
      }
    ],
    tips: [
      'Double cleanse only at night to remove makeup and sunscreen',
      'Use lukewarm water - hot water strips natural oils',
      'Massage oil cleanser for 60 seconds to dissolve impurities',
      'Pat dry gently, don\'t rub'
    ],
    amPm: {
      am: 'Gentle foam or water-based cleanser only',
      pm: 'Oil cleanser first, then foam cleanser (double cleanse)'
    }
  },
  {
    id: 'prep',
    step: 2,
    name: 'Prep',
    icon: '💧',
    color: '#34D399',
    briefDescription: 'Balance pH and boost hydration',
    fullDescription: 'Prep products restore your skin\'s pH balance after cleansing and create a "water path" for better absorption of subsequent products. Korean toners are typically hydrating, unlike Western astringent toners.',
    products: [
      {
        name: 'Toner',
        types: ['Hydrating Toner', 'Essence Toner', 'First Treatment Essence'],
        description: 'Balances pH, removes residue, preps skin for absorption'
      },
      {
        name: 'Skin Pad',
        types: ['Toner Pads', 'Exfoliating Pads', 'Calming Pads'],
        description: 'Convenient toner-soaked pads for on-the-go prep'
      },
      {
        name: 'Facial Mist',
        types: ['Hydrating Mist', 'Setting Mist', 'Refreshing Mist'],
        description: 'Can be used throughout the day for hydration boost'
      }
    ],
    tips: [
      'Apply toner within 3 seconds of cleansing (3-second rule)',
      'Try the "7-skin method" - layer 3-7 thin layers of toner for intense hydration',
      'Pat products in rather than rubbing',
      'Fermented ingredients (galactomyces) enhance absorption'
    ],
    amPm: {
      am: 'Hydrating toner or essence to prep for serum and sunscreen',
      pm: 'Toner followed by treatment products'
    }
  },
  {
    id: 'nourish',
    step: 3,
    name: 'Nourish',
    icon: '✨',
    color: '#A78BFA',
    briefDescription: 'Target concerns with active ingredients',
    fullDescription: 'This is where you address specific skin concerns with concentrated treatments. Layer from thinnest to thickest consistency, allowing each product to absorb before the next. This layering technique maximizes the benefits of each product.',
    products: [
      {
        name: 'Serum / Ampoule',
        types: ['Brightening Serum', 'Anti-aging Serum', 'Hydrating Ampoule', 'Acne Serum'],
        description: 'Concentrated active ingredients for targeted treatment'
      },
      {
        name: 'Sheet Mask',
        types: ['Hydrating Mask', 'Brightening Mask', 'Calming Mask', 'Anti-aging Mask'],
        description: 'Weekly treatment for intense nourishment (15-20 min)'
      },
      {
        name: 'Eye Cream',
        types: ['Anti-wrinkle Eye Cream', 'Brightening Eye Cream', 'Hydrating Eye Cream'],
        description: 'Delicate eye area needs specialized care'
      },
      {
        name: 'Moisturizer',
        types: ['Gel Cream', 'Water Cream', 'Rich Cream', 'Sleeping Mask'],
        description: 'Seals in hydration and creates protective barrier'
      },
      {
        name: 'Face Oil',
        types: ['Hydrating Oil', 'Treatment Oil', 'Facial Oil Blend'],
        description: 'Final step to lock in moisture (optional)'
      },
      {
        name: 'Spot Treatment',
        types: ['Acne Patch', 'Dark Spot Treatment', 'Pimple Cream'],
        description: 'Target specific problem areas'
      }
    ],
    tips: [
      'Layer from thinnest to thickest consistency',
      'Wait 30-60 seconds between products for absorption',
      'Use sheet masks 1-2 times per week',
      'Pat remaining mask essence into skin after removing',
      'Eye cream should be applied with ring finger (gentlest pressure)'
    ],
    amPm: {
      am: 'Lighter serums, moisturizer (skip heavy treatments)',
      pm: 'Full treatment routine - serums, masks, eye cream, rich moisturizer'
    }
  },
  {
    id: 'protect',
    step: 4,
    name: 'Protect',
    icon: '☀️',
    color: '#FBBF24',
    briefDescription: 'Shield from UV and environmental damage',
    fullDescription: 'Sun protection is non-negotiable in K-beauty. UV exposure is the #1 cause of premature aging, hyperpigmentation, and skin damage. Korean sunscreens are known for their lightweight, elegant formulas that work well under makeup.',
    products: [
      {
        name: 'Sunscreen',
        types: ['Sun Cream', 'Sun Milk', 'Sun Stick', 'Sun Cushion', 'Tone-up Sunscreen'],
        description: 'SPF 50+ PA++++ recommended for daily use'
      },
      {
        name: 'Facial Mist',
        types: ['SPF Mist', 'Hydrating Mist'],
        description: 'For reapplication throughout the day'
      }
    ],
    tips: [
      'Apply sunscreen as the LAST step of skincare (before makeup)',
      'Use 2 finger-lengths of sunscreen for face',
      'Reapply every 2 hours when outdoors',
      'Use even on cloudy days - UV penetrates clouds',
      'Don\'t forget ears, neck, and hands!'
    ],
    amPm: {
      am: 'Sunscreen is ESSENTIAL - never skip!',
      pm: 'No sunscreen needed at night'
    }
  }
]

// Toner vs Essence vs Serum comparison
export interface ProductComparison {
  category: string
  order: string
  purpose: string
  texture: string
  keyIngredients: string[]
  frequency: string
  kbeautyTip: string
  example: string
}

export const PRODUCT_COMPARISONS: ProductComparison[] = [
  {
    category: 'Toner',
    order: 'Right after cleansing (1st)',
    purpose: 'Balances pH, removes residue, preps skin for absorption, provides instant hydration',
    texture: 'Watery to slightly viscous',
    keyIngredients: ['Centella (Cica)', 'Hyaluronic Acid', 'AHA/BHA (in small amounts)'],
    frequency: 'Daily, AM & PM',
    kbeautyTip: 'Try the "7-skin method" - layer multiple thin applications for deep hydration',
    example: 'Anua Heartleaf 77% Soothing Toner'
  },
  {
    category: 'Essence',
    order: 'After toner (2nd)',
    purpose: 'Boosts skin vitality, provides basic hydration and nutrition, prepares for next steps',
    texture: 'Lighter than serum, more viscous than toner',
    keyIngredients: ['Galactomyces (fermented)', 'Vitamins', 'Hyaluronic Acid', 'Plant extracts'],
    frequency: 'Daily, AM & PM (optional)',
    kbeautyTip: 'Creates a "water path" to help subsequent products absorb better',
    example: 'COSRX Advanced Snail 96 Mucin Power Essence'
  },
  {
    category: 'Serum',
    order: 'After essence (3rd)',
    purpose: 'Intensive treatment for specific concerns (brightening, anti-aging, acne)',
    texture: 'More viscous and concentrated (ampoules are even more concentrated)',
    keyIngredients: ['Vitamin C', 'Retinol', 'Peptides', 'Niacinamide', 'Salicylic Acid'],
    frequency: 'Daily, AM &/or PM (as needed)',
    kbeautyTip: 'Most concentrated "treatment" product - choose based on your specific skin concern',
    example: 'Goodal Green Tangerine Vita C Dark Spot Care Serum'
  }
]

// Routine types
export interface RoutineType {
  id: string
  name: string
  description: string
  steps: number
  bestFor: string[]
}

export const ROUTINE_TYPES: RoutineType[] = [
  {
    id: 'essential',
    name: 'Essential Routine',
    description: 'The foundational K-beauty routine with core steps for everyday skin health.',
    steps: 4,
    bestFor: ['Beginners', 'Busy lifestyles', 'Minimalists', 'Normal skin']
  },
  {
    id: 'deep-dive',
    name: 'Deep Dive (Targeted Treatment)',
    description: 'Comprehensive routine with targeted treatments for specific skin concerns.',
    steps: 7,
    bestFor: ['Specific skin concerns', 'Anti-aging goals', 'Acne-prone skin', 'Hyperpigmentation']
  },
  {
    id: 'quick-easy',
    name: 'Quick & Easy',
    description: 'Streamlined routine using multi-functional products like all-in-ones.',
    steps: 3,
    bestFor: ['Very busy schedules', 'Travel', 'Skin that needs less intervention']
  }
]

// Key K-beauty ingredients by category
export const KBEAUTY_INGREDIENTS = {
  regeneration: {
    title: 'Regeneration & Anti-aging',
    icon: '🔄',
    ingredients: [
      { name: 'PDRN', benefit: 'Skin regeneration, collagen boost, elasticity' },
      { name: 'Peptides', benefit: 'Collagen protection, firmness, fine lines' },
      { name: 'Exosome', benefit: 'Cell regeneration, anti-aging' },
      { name: 'Retinol/Retinal', benefit: 'Cell turnover, wrinkles, pore minimizing' }
    ]
  },
  pigmentation: {
    title: 'Pigmentation & Tone',
    icon: '✨',
    ingredients: [
      { name: 'Vitamin C', benefit: 'Brightening, antioxidant, dark spots' },
      { name: 'Niacinamide', benefit: 'Tone evening, pore minimizing, barrier' },
      { name: 'Tranexamic Acid', benefit: 'Melasma, dark spots, brightening' },
      { name: 'Arbutin', benefit: 'Gentle brightening, hyperpigmentation' }
    ]
  },
  barrier: {
    title: 'Barrier & Soothing',
    icon: '🛡️',
    ingredients: [
      { name: 'Centella/Cica', benefit: 'Calming, healing, redness relief' },
      { name: 'Ceramides', benefit: 'Barrier repair, moisture retention' },
      { name: 'Panthenol', benefit: 'Soothing, hydration, healing' },
      { name: 'Beta-Glucan', benefit: 'Calming, hydration, barrier support' }
    ]
  },
  hydration: {
    title: 'Hydration',
    icon: '💧',
    ingredients: [
      { name: 'Hyaluronic Acid', benefit: 'Deep hydration, plumping' },
      { name: 'Polyglutamic Acid', benefit: 'Superior moisture retention' },
      { name: 'Snail Mucin', benefit: 'Hydration, repair, scar healing' },
      { name: 'Squalane', benefit: 'Lightweight moisture, barrier support' }
    ]
  }
}
